import { useState, useEffect, Suspense } from 'react';
import { Heart, X } from 'lucide-react';
import { RSVPData, Guest, WeddingInvitation } from '../types/invitation';
import { publicInvitationService } from '../lib/supabase';
import { useSEO } from '../hooks/useSEO';
import { generateInvitationTitle, generatePageTitle, generateMetaDescription } from '../lib/brandUtils';
import { generateMapUrl, hexToRgbChannels, formatChineseDate } from '../lib/utils';
import { invitationTemplates } from './wizard/data';
import { getLayoutComponent } from './invite-layouts';
import { createLogger } from '../lib/logger';

const log = createLogger('invitation');

// 時間格式化函數：移除秒數部分
const formatTime = (timeString?: string): string => {
  if (!timeString) return '';
  // 如果時間格式包含秒數 (HH:MM:SS)，只取前5個字符 (HH:MM)
  return timeString.length > 5 ? timeString.substring(0, 5) : timeString;
};

interface WeddingInfo {
  brideName: string;
  groomName: string;
  date: string;
  time: string;
  venue: {
    name: string;
    address: string;
    mapUrl: string;
  };
  reception: {
    name: string;
    address: string;
    time: string;
  };
  contact: {
    phone: string;
    email: string;
  };
  coverImage: string;
}

interface GuestInfo {
  name: string;
  email: string;
  phone: string;
}

interface InvitePageProps {
  inviteCode: string;
  onRsvpSubmit?: (submittedInviteCode: string, rsvpData: RSVPData, guestInfo?: GuestInfo) => void;
  weddingInvitation?: WeddingInvitation;
}

export const InvitePage: React.FC<InvitePageProps> = ({ 
  inviteCode, 
  onRsvpSubmit, 
  weddingInvitation: propWeddingInvitation
}) => {
  const [guest, setGuest] = useState<Guest | null>(null);
  // const [isNewGuest, setIsNewGuest] = useState(false);
  const [weddingInvitation, setWeddingInvitation] = useState<WeddingInvitation | null>(propWeddingInvitation || null);
  const [loading, setLoading] = useState(!propWeddingInvitation);
  const [error, setError] = useState<string | null>(null);

  // 從 weddingInvitation 狀態生成 weddingInfo
  const weddingInfo: WeddingInfo | null = weddingInvitation
    ? {
        brideName: weddingInvitation.brideName,
        groomName: weddingInvitation.groomName,
        date: weddingInvitation.weddingDate,
        time: weddingInvitation.weddingTime,
        venue: {
          name: weddingInvitation.venue,
          address: weddingInvitation.address,
          mapUrl: generateMapUrl(weddingInvitation.mapUrl, weddingInvitation.venue, weddingInvitation.address)
        },
        reception: {
          name: weddingInvitation.receptionVenue || weddingInvitation.venue,
          address: weddingInvitation.receptionAddress || weddingInvitation.address,
          time: weddingInvitation.receptionTime || weddingInvitation.weddingTime
        },
        contact: {
          phone: weddingInvitation.contactPhone || '0912-345-678',
          email: weddingInvitation.contactEmail || 'wedding@example.com'
        },
        coverImage: weddingInvitation.coverImage || 'https://images.unsplash.com/photo-1519741497674-611481863552?ixlib=rb-4.0.3&auto=format&fit=crop&w=2370&q=80'
      }
    : null;

  const templateData = invitationTemplates.find(
    t => t.name === weddingInvitation?.template || t.style === weddingInvitation?.template
  ) || invitationTemplates.find(t => t.style === 'classic');
  const themeStyle = {
    '--theme-primary-rgb': hexToRgbChannels(weddingInvitation?.primaryColor || '#E6A4B4'),
    '--theme-secondary-rgb': hexToRgbChannels(templateData?.secondaryColor || '#C0A06F'),
  } as React.CSSProperties;

  const [showRSVP, setShowRSVP] = useState(false);
  const [isWeddingPast, setIsWeddingPast] = useState(false);
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0
  });

  // 載入邀請函資料
  useEffect(() => {
    if (!propWeddingInvitation && inviteCode) {
      const loadInvitation = async () => {
        try {
          setLoading(true);
          setError(null);
          const invitation = await publicInvitationService.getInvitationByCode(inviteCode);
          
          if (invitation) {
            setWeddingInvitation(invitation);
          } else {
            // 嘗試從 localStorage 載入作為回退
            try {
              const savedInvitations = localStorage.getItem('weddingInvitations');
              if (savedInvitations) {
                const invitations: WeddingInvitation[] = JSON.parse(savedInvitations);
                const matchingInvitation = invitations.find(inv => inv.inviteCode === inviteCode);
                if (matchingInvitation) {
                  setWeddingInvitation(matchingInvitation);
                } else {
                  setError('找不到邀請函');
                }
              } else {
                setError('找不到邀請函');
              }
            } catch (localError) {
              setError('找不到邀請函');
            }
          }
        } catch (error) {
          log.error('Error loading invitation:', error);
          setError('載入邀請函時發生錯誤');
        } finally {
          setLoading(false);
        }
      };

      loadInvitation();
    }
  }, [inviteCode, propWeddingInvitation]);

  // SEO 優化
  const currentInvitation = weddingInvitation || propWeddingInvitation;
  const seoTitle = currentInvitation 
    ? generateInvitationTitle(currentInvitation.brideName, currentInvitation.groomName)
    : generatePageTitle();
  
  const seoDescription = currentInvitation
    ? `誠摯邀請您參加 ${currentInvitation.brideName} 與 ${currentInvitation.groomName} 的婚禮慶典，${formatChineseDate(currentInvitation.weddingDate)} 於 ${currentInvitation.venue}`
    : generateMetaDescription();

  const seoImage = currentInvitation?.coverImage || 'https://images.unsplash.com/photo-1519741497674-611481863552?ixlib=rb-4.0.3&auto=format&fit=crop&w=2370&q=80';

  useSEO({
    title: seoTitle,
    description: seoDescription,
    keywords: '婚禮邀請函, 婚禮規劃, 線上邀請, RSVP, 婚宴',
    ogTitle: seoTitle,
    ogDescription: seoDescription,
    ogImage: seoImage,
    ogUrl: `${window.location.origin}/invite/${inviteCode}`
  });

  // 模擬根據邀請碼檢查是否有關聯的賓客
  useEffect(() => {
    const existingGuests = ['abc123', 'def456']; // 模擬已有賓客關聯的邀請碼
    
    if (existingGuests.includes(inviteCode)) {
      // 找到現有賓客
      const mockGuest = {
        id: '1',
        name: '王小華',
        email: 'guest@example.com',
        group: '大學同學'
      };
      setGuest(mockGuest);
      // setIsNewGuest(false);
    } else {
      // 新訪客，需要收集資訊
      // setIsNewGuest(true);
      setGuest(null);
    }

    // 計算倒數計時
    const calculateTimeLeft = () => {
      // 檢查 weddingInfo 是否存在
      if (!weddingInfo) {
        log.debug('weddingInfo 不存在');
        setTimeLeft({ days: 0, hours: 0, minutes: 0 });
        setIsWeddingPast(false);
        return;
      }
      
      log.debug('weddingInfo.date =', weddingInfo.date);
      log.debug('weddingInfo.time =', weddingInfo.time);
      
      // 使用真實婚禮日期
      if (!weddingInfo.date || weddingInfo.date === 'YYYY-MM-DD') {
        // 如果沒有設定有效日期，不顯示倒數計時
        log.debug('無效的婚禮日期');
        setTimeLeft({ days: 0, hours: 0, minutes: 0 });
        setIsWeddingPast(false);
        return;
      }
      
      // 處理時間格式，移除多餘的秒數
      const timeFormatted = weddingInfo.time.length > 5 ? weddingInfo.time.substring(0, 5) : weddingInfo.time;
      log.debug('timeFormatted =', timeFormatted);
      const weddingDate = new Date(`${weddingInfo.date}T${timeFormatted}:00`);
      const now = new Date();
      log.debug('weddingDate =', weddingDate);
      log.debug('now =', now);
      
      const difference = weddingDate.getTime() - now.getTime();
      log.debug('difference =', difference);

      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / 1000 / 60) % 60)
        });
      } else {
        setIsWeddingPast(true);
        setTimeLeft({ days: 0, hours: 0, minutes: 0 });
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);
    return () => clearInterval(timer);
  }, [inviteCode, weddingInvitation?.weddingDate, weddingInvitation?.weddingTime]);

  const addToCalendar = () => {
    if (!weddingInfo) return;
    
    // 檢查是否有有效的日期
    if (!weddingInfo.date || weddingInfo.date === 'YYYY-MM-DD') {
      alert('婚禮日期資訊不完整，無法加入行事曆');
      return;
    }
    
    // 收集所有活動時間，決定最早開始時間和最晚結束時間
    const activities = [];
    
    // 迎賓活動
    if (weddingInvitation?.hasWelcomeParty === true && weddingInvitation?.welcomePartyTime) {
      activities.push({
        name: '迎賓',
        time: weddingInvitation.welcomePartyTime,
        venue: weddingInvitation.welcomePartyVenue || weddingInfo.venue.name
      });
    }
    
    // 證婚儀式
    if (weddingInvitation?.hasWeddingCeremony === true || !weddingInvitation) {
      const ceremonyTime = weddingInvitation?.ceremonyTime || weddingInfo.time;
      if (ceremonyTime) {
        activities.push({
          name: '證婚儀式',
          time: ceremonyTime,
          venue: weddingInvitation?.ceremonyVenue || weddingInfo.venue.name
        });
      }
    }
    
    // 婚宴
    if (weddingInvitation?.hasReception === true || !weddingInvitation) {
      const receptionTime = weddingInvitation?.receptionTime || weddingInfo.reception?.time;
      if (receptionTime) {
        activities.push({
          name: '婚宴',
          time: receptionTime,
          venue: weddingInvitation?.receptionVenue || weddingInfo.reception?.name
        });
      }
    }
    
    // 如果沒有任何活動時間，使用基本時間
    if (activities.length === 0) {
      if (weddingInfo.time) {
        activities.push({
          name: '婚禮',
          time: weddingInfo.time,
          venue: weddingInfo.venue.name
        });
      } else {
        alert('沒有找到有效的活動時間，無法加入行事曆');
        return;
      }
    }
    
    // 找出最早和最晚的時間
    const times = activities.map(activity => {
      const timeFormatted = formatTime(activity.time);
      const dateTime = new Date(`${weddingInfo.date}T${timeFormatted}:00`);
      return { ...activity, dateTime };
    }).filter(activity => !isNaN(activity.dateTime.getTime()));
    
    if (times.length === 0) {
      alert('活動時間格式無效，無法加入行事曆');
      return;
    }
    
    // 計算開始和結束時間
    const startDateTime = new Date(Math.min(...times.map(t => t.dateTime.getTime())));
    const lastActivity = new Date(Math.max(...times.map(t => t.dateTime.getTime())));
    
    // 結束時間：最後活動開始後4小時
    const endDateTime = new Date(lastActivity.getTime() + 4 * 60 * 60 * 1000);
    
    // 轉換為 Google Calendar 所需的格式 (YYYYMMDDTHHMMSSZ)
    const formatDateForCalendar = (date: Date): string => {
      // 獲取本地時間而不是 UTC 時間
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      
      return `${year}${month}${day}T${hours}${minutes}${seconds}`;
    };
    
    // 生成活動描述
    let description = `誠摯邀請您參加我們的婚禮慶典\n\n活動安排：\n`;
    activities.forEach(activity => {
      description += `• ${formatTime(activity.time)} ${activity.name}`;
      if (activity.venue) {
        description += ` - ${activity.venue}`;
      }
      description += '\n';
    });
    
    const startFormatted = formatDateForCalendar(startDateTime);
    const endFormatted = formatDateForCalendar(endDateTime);
    
    // 調試資訊 - 可以在開發時取消註解
    log.debug('行事曆時間調試:', {
      activities: activities.map(a => ({ name: a.name, time: a.time })),
      startDateTime: startDateTime.toLocaleString(),
      endDateTime: endDateTime.toLocaleString(),
      startFormatted,
      endFormatted
    });
    
    const event = {
      title: `${weddingInfo.brideName} & ${weddingInfo.groomName} 的婚禮`,
      start: startFormatted,
      end: endFormatted,
      location: weddingInfo.venue.address,
      description: description
    };
    
    const googleCalendarUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(event.title)}&dates=${event.start}/${event.end}&location=${encodeURIComponent(event.location)}&details=${encodeURIComponent(event.description)}`;
    window.open(googleCalendarUrl, '_blank');
  };


  // 載入狀態
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-pink-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">載入邀請函中...</p>
        </div>
      </div>
    );
  }

  // 錯誤狀態
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="bg-red-100 rounded-full p-4 w-32 h-32 flex items-center justify-center mx-auto mb-4">
            <X className="h-16 w-16 text-red-500" />
          </div>
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">載入失敗</h2>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  // 沒有邀請函資料
  if (!weddingInfo) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="bg-gray-100 rounded-full p-4 w-32 h-32 flex items-center justify-center mx-auto mb-4">
            <Heart className="h-16 w-16 text-gray-400" />
          </div>
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">找不到邀請函</h2>
          <p className="text-gray-600">請確認您的邀請連結是否正確</p>
        </div>
      </div>
    );
  }

  const LayoutComponent = getLayoutComponent(templateData?.style || 'classic');

  return (
    <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-32 w-32 border-b-2 border-pink-500 mx-auto" /></div>}>
      <LayoutComponent
        weddingInvitation={weddingInvitation!}
        weddingInfo={weddingInfo}
        templateData={templateData || invitationTemplates[0]}
        themeStyle={themeStyle}
        countdown={timeLeft}
        isPastWedding={isWeddingPast}
        showRSVP={showRSVP}
        setShowRSVP={setShowRSVP}
        onRsvpSubmit={onRsvpSubmit}
        addToCalendar={addToCalendar}
        guest={guest}
        inviteCode={inviteCode}
      />
    </Suspense>
  );
};